import { createBrowserRouter, Navigate } from "react-router-dom";
import { Suspense } from "react";

import ErrorBoundary from "@components/common/ErrorBoundary";
import LoadingFallback from "@components/common/LoadingFallback";
import RouteWrapper from "@components/common/RouteWrapper";

import ProtectedRoute from "@components/layout/ProtectedRoute";
import RoleProtectedRoute from "@components/layout/RoleProtectedRoute";
import PublicRoute from "@components/layout/PublicRoute";
import Layout from "@components/layout/Layout";

import routeConfigs from "@routes/route-configs";
import { UserRole } from "@/types/user";

// Helper function to determine default route based on user role
const getDefaultRoute = (userRole?: UserRole) => {
  if (userRole === UserRole.CLIENT || userRole === UserRole.VIEWER) {
    return "/dashboard";
  }
  return "/surveys";
};

export const router = createBrowserRouter([
  // Public routes
  ...routeConfigs
    .filter((route) => !route.protected)
    .map(({ path, component: Component, loadingMessage, allowUnverified }) => ({
      path,
      element: (
        <ErrorBoundary>
          <Suspense
            fallback={<LoadingFallback message="Loading application..." />}
          >
            <PublicRoute allowUnverified={allowUnverified}>
              <RouteWrapper loadingMessage={loadingMessage}>
                <Component />
              </RouteWrapper>
            </PublicRoute>
          </Suspense>
        </ErrorBoundary>
      ),
    })),

  // Protected routes with layout
  {
    path: "/",
    element: (
      <ErrorBoundary>
        <Suspense
          fallback={<LoadingFallback message="Loading application..." />}
        >
          <ProtectedRoute />
        </Suspense>
      </ErrorBoundary>
    ),
    children: [
      {
        path: "/",
        element: <Layout />,
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          ...routeConfigs
            .filter((route) => route.protected && route.layout)
            .map(
              ({
                path,
                component: Component,
                loadingMessage,
                allowedRoles,
              }) => ({
                path: path.startsWith("/") ? path.slice(1) : path, // Remove leading slash for nested routes
                element: allowedRoles ? (
                  <RoleProtectedRoute
                    allowedRoles={allowedRoles}
                    fallbackRoute="/dashboard"
                  >
                    <RouteWrapper loadingMessage={loadingMessage}>
                      <Component />
                    </RouteWrapper>
                  </RoleProtectedRoute>
                ) : (
                  <RouteWrapper loadingMessage={loadingMessage}>
                    <Component />
                  </RouteWrapper>
                ),
              }),
            ),
        ],
      },
      {
        path: "*",
        element: <Navigate to={getDefaultRoute()} replace />,
      },
    ],
  },
]);
